import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Eye, Users, ChevronDown, Download } from 'lucide-react';
import ReportService from '../services/ReportService';
import Table from '../components/ui/Table';
import Modal from '../components/ui/Modal';
import ConfirmModal from '../components/ui/ConfirmModal';
import Loader from '../components/ui/Loader';
import { useToast } from '../contexts/ToastContext';
import { classService } from '../services/classService';
import APIRequest from '../api/APIRequest';
import { useSelector } from 'react-redux';
import CustomDatePicker from '../components/ui/CustomDatePicker';

const Classes = () => {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [classes, setClasses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const academicYearToRef = useRef(null);
  const [isSectionDropdownOpen, setIsSectionDropdownOpen] = useState(false);
  const sectionDropdownRef = useRef(null);
  const [isTermsDropdownOpen, setIsTermsDropdownOpen] = useState(false);
  const termsDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sectionDropdownRef.current && !sectionDropdownRef.current.contains(event.target)) {
        setIsSectionDropdownOpen(false);
      }
      if (termsDropdownRef.current && !termsDropdownRef.current.contains(event.target)) {
        setIsTermsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load classes from service
  useEffect(() => {
     (async () => { 
      const classes = await classService.getAllClasses(user.token);
      setClasses(classes);
     })();
  }, [user.token]);

  const [formData, setFormData] = useState({
    name: '',
    numberOfSections: 0,
    section: '',
    class_capacity: '',
    feesPerAcademicYear: '',
    numberOfTerms: '',
    inchargeId: '',
    termFees: [{ amount: '', dueDate: '' }],
    academicYearFrom: '',
    academicYearTo: ''
  });

  const filteredClasses = (Array.isArray(classes) ? classes : []).filter(cls =>
    cls?.name?.toLowerCase().includes(searchTerm?.toLowerCase() || '') ||
    cls?.section?.toLowerCase().includes(searchTerm?.toLowerCase() || '') ||
    (cls?.inchargeName && cls?.inchargeName?.toLowerCase().includes(searchTerm?.toLowerCase() || ''))
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredClasses?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedClasses = filteredClasses?.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const resetForm = () => {
    setFormData({
      name: '',
      numberOfSections: 0,
      section: '',
      class_capacity: '',
      feesPerAcademicYear: '',
      numberOfTerms: '',
      inchargeId: '',
      termFees: [{ amount: '', dueDate: '' }],
      academicYearFrom: '',
      academicYearTo: ''
    });
  };

  const calculateDueDates = (fromDateStr, toDateStr, nTerms) => {
    const fromDate = fromDateStr ? new Date(fromDateStr) : null;
    const toDate = toDateStr ? new Date(toDateStr) : null;
    const n = nTerms ? Number(nTerms) : 0;
    
    if (fromDate && toDate && n > 0) {
      const duration = toDate.getTime() - fromDate.getTime();
      const intervalMs = duration / Math.max(n, 1);
      const intervals = [];
      for (let i = 0; i < n; i++) {
        const dueDate = new Date(fromDate.getTime() + (i * intervalMs));
        intervals.push(dueDate.toISOString().split('T')[0]);
      }
      return intervals;
    }
    return [];
  };

  const handleNumberOfTermsChange = (newNumberOfTerms) => {
    const totalFees = formData.feesPerAcademicYear ? Number(formData.feesPerAcademicYear) : 0;
    const nTerms = newNumberOfTerms ? Number(newNumberOfTerms) : 0;
    
    const feePerTerm = totalFees > 0 && nTerms > 0 ? 
      Math.round((totalFees / nTerms) * 100) / 100 : '';
    
    const intervals = calculateDueDates(formData.academicYearFrom, formData.academicYearTo, nTerms);
    const currentTermFees = [];

    for (let i = 0; i < nTerms; i++) {
      const existingTermFee = formData.termFees[i];
      currentTermFees.push({
        amount: feePerTerm,
        dueDate: (intervals[i] || (existingTermFee && existingTermFee.dueDate) || '')
      });
    }
    
    setFormData({
      ...formData,
      numberOfTerms: newNumberOfTerms,
      termFees: currentTermFees
    });
  };

  const handleTermFeeChange = (termIndex, field, value) => {
    const updatedTermFees = [...formData.termFees];
    updatedTermFees[termIndex] = {
      ...updatedTermFees[termIndex],
      [field]: value
    };
    
    if (field === 'amount') {
      const totalFees = updatedTermFees.reduce((sum, term) => sum + (term.amount ? Number(term.amount) : 0), 0);
      setFormData({
        ...formData,
        termFees: updatedTermFees,
        feesPerAcademicYear: totalFees || ''
      });
    } else {
      setFormData({
        ...formData,
        termFees: updatedTermFees
      });
    }
  };

  const handleTotalFeesChange = (newTotalFees) => {
    const totalFees = newTotalFees === '' ? 0 : Number(newTotalFees);
    const nTerms = formData.numberOfTerms ? Number(formData.numberOfTerms) : 0;
    
    const feePerTerm = totalFees > 0 && nTerms > 0 ? 
      Math.round((totalFees / nTerms) * 100) / 100 : '';
    
    const updatedTermFees = formData.termFees.map(termFee => ({
      ...termFee,
      amount: feePerTerm
    }));
    
    setFormData({
      ...formData,
      feesPerAcademicYear: newTotalFees,
      termFees: updatedTermFees
    });
  };

  const handleAcademicYearChange = (field, value) => {
    const newFrom = field === 'academicYearFrom' ? value : formData.academicYearFrom;
    const newTo = field === 'academicYearTo' ? value : formData.academicYearTo;
    const nTerms = formData.numberOfTerms ? Number(formData.numberOfTerms) : 0;
    
    const intervals = calculateDueDates(newFrom, newTo, nTerms);
    
    const updatedTermFees = formData.termFees.map((term, i) => ({
      ...term,
      dueDate: term.dueDate && !intervals[i] ? term.dueDate : (intervals[i] || term.dueDate || '')
    }));

    setFormData({
      ...formData,
      [field]: value,
      termFees: updatedTermFees
    });

    if (field === 'academicYearFrom') {
      setTimeout(() => academicYearToRef.current?.open(), 100);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate academic year dates
    if (formData.academicYearFrom && formData.academicYearTo) {
      const fromDate = new Date(formData.academicYearFrom);
      const toDate = new Date(formData.academicYearTo);
      
      if (toDate < fromDate) {
        error('Academic Year To date must be greater than or equal to Academic Year From date.');
        return;
      }
    }
    
    // Validate all required fields
    if (!formData.name || !formData.class_capacity || 
        !formData.feesPerAcademicYear || !formData.numberOfTerms || 
        !formData.academicYearFrom || !formData.academicYearTo) {
      error('Please fill in all required fields.');
      return;
    }
    
    // Check for duplicate class with same academic year
    const fromTime = new Date(formData.academicYearFrom).getTime();
    const toTime = new Date(formData.academicYearTo).getTime();
    
    const duplicateClass = classes.find(cls => 
      cls.name === formData.name && 
      cls.section === formData.section && 
      cls.academicYearFrom === fromTime && 
      cls.academicYearTo === toTime
    );
    
    if (duplicateClass) {
      error(`Class ${formData.name} Section ${formData.section} already exists for this academic year.`);
      return;
    }
    
    setIsCreating(true);
    
    const newClass = {
      name: formData.name,
      numberOfSections: formData.numberOfSections,
      section: formData.section,
      class_capacity: formData.class_capacity,
      feesPerAcademicYear: formData.feesPerAcademicYear,
      numberOfTerms: formData.numberOfTerms,
      termFees: formData.termFees,
      academicYearFrom: formData.academicYearFrom ? new Date(formData.academicYearFrom).getTime() : null,
      academicYearTo: formData.academicYearTo ? new Date(formData.academicYearTo).getTime() : null
    };
    const api = new APIRequest();
    try { 
      const response = await api.request('/admin/createClass', 'POST', newClass, user.token);
      if(response.success){
        success(response.message || 'Class added successfully!');
        setIsModalOpen(false);
        resetForm();
        classService.addClass(response.class);
        const updatedClasses = await classService.getAllClasses(user.token);
        setClasses(updatedClasses);
      }else{
        error('Failed to add class. Please try again.');
      }
    } catch (err) {
      console.error('Error adding class:', err);
      error('Failed to add class. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleView = async (cls) => {
    setIsCreating(true);
    try {
      const api = new APIRequest();
      const response = await api.request(`/admin/getClass?id=${cls._id}`, 'GET', {}, user.token);
      
      if (response.success) {
        navigate(`/classes/${cls._id}`, { state: { classData: response.class } });
      } else {
        error(response.message || 'Failed to fetch class details. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching class data:', err);
      error('Failed to fetch class details. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleEdit = async (cls) => {
    setIsCreating(true);
    try {
      const api = new APIRequest();
      const response = await api.request(`/admin/getClass?id=${cls._id}`, 'GET', {}, user.token);
      
      if (response.success) {
        navigate(`/classes/${cls._id}/edit`, { state: { classData: response.class } });
      } else {
        error(response.message || 'Failed to fetch class details. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching class data:', err);
      error('Failed to fetch class details. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = (id) => {
    setClassToDelete(id);
    setIsConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!classToDelete) return;
    
    try {
      const response = await classService.deleteClass(classToDelete, user.token);
      if (response.success) {
        success('Class deleted successfully!');
        const updatedClasses = await classService.getAllClasses(user.token);
        setClasses(updatedClasses);
      } else {
        error(response.message || 'Failed to delete class');
      }
    } catch (err) {
      console.error('Error deleting class:', err);
      error('Failed to delete class');
    } finally {
      setIsConfirmModalOpen(false);
      setClassToDelete(null);
    }
  };


  const columns = [
    {
      key: 'name',
      label: 'Class Details',
      render: (cls) => (
        <div>
          <p className="font-medium">{cls?.name}</p>
          <p className="text-sm text-gray-600">Section {cls?.section}</p>
        </div>
      )
    },
    {
      key: 'incharge',
      label: 'Class Incharge',
      render: (cls) => (
        <div>
          {cls?.classInchargeName ? (
            <p className="text-sm">{cls?.classInchargeName}</p>
          ) : (
            <p className="text-sm text-gray-500">Not assigned</p>
          )}
        </div>
      )
    },
    {
      key: 'enrollment',
      label: 'Enrollment',
      render: (cls) => {
        const enrollmentCount = cls?.actualStudentCount !== undefined 
          ? cls.actualStudentCount 
          : (cls?.enrolledStudents?.length || 0);
        const capacity = cls?.class_capacity || 0;
        return (
          <div className="flex items-center space-x-2">
            <Users size={16} className="text-gray-400" />
            <span>{enrollmentCount}/{capacity}</span>
            <div className="w-12 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-orange-500 h-2 rounded-full transition-all"
                style={{ width: `${capacity > 0 ? ((enrollmentCount / capacity) * 100) : 0}%` }}
              ></div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'academicYear',
      label: 'Academic Year',
      render: (cls) => (
        <div>
          {cls?.academicYearFrom && cls?.academicYearTo ? (
            <div>
              <p className="text-sm font-medium text-gray-900">
                {new Date(cls.academicYearFrom).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </p>
              <p className="text-xs text-gray-500">to</p>
              <p className="text-sm font-medium text-orange-600">
                {new Date(cls.academicYearTo).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Not set</p>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
        render: (cls) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleView(cls)}
            className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
            title="View"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={() => handleEdit(cls)}
            className="p-1 text-green-600 hover:text-green-800 transition-colors"
            title="Edit"
          >
            <Edit2 size={16} />
          </button>
          <button
            onClick={() => handleDelete(cls._id)}
            className="p-1 text-red-600 hover:text-red-800 transition-colors"
            title="Delete"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )
    }
  ];


  return (
    <div className="space-y-8 animate-fadeIn max-w-[1600px] mx-auto">
      {/* Modern Header Section */}
      <div className="glass-card rounded-[2.5rem] p-8 px-10 flex flex-col md:flex-row justify-between items-center gap-6 premium-shadow">
        <div className="text-center md:text-left">
          <h1 className="text-4xl font-black tracking-tight text-[#1e293b]">
            <span className="bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">Classes</span>
          </h1>
          <p className="text-slate-500 mt-2 font-medium flex items-center justify-center md:justify-start gap-2">
            <Users size={18} className="text-[#1B6CA8]" />
            Manage school classes and sections
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          <div className="flex bg-slate-100 rounded-2xl p-1 shadow-sm">
            <button
              onClick={() => {
                const columns = [
                  { header: 'Class Name', dataKey: 'name' },
                  { header: 'Section', dataKey: 'section' },
                  { header: 'Incharge', dataKey: 'incharge' },
                  { header: 'Capacity', dataKey: 'capacity' },
                  { header: 'Enrollment', dataKey: 'enrollment' }
                ];
                const data = filteredClasses.map(cls => ({
                  name: cls.name,
                  section: cls.section,
                  incharge: cls.classInchargeName || 'N/A',
                  capacity: cls.class_capacity || 0,
                  enrollment: cls.actualStudentCount || 0
                }));
                ReportService.generatePDF('Classes Directory', columns, data, 'classes_directory.pdf');
              }}
              className="px-4 py-2.5 text-[#1e293b] hover:bg-white rounded-xl transition-all duration-200 flex items-center gap-2 font-bold text-sm"
            >
              <Download size={16} className="text-blue-600" />
              <span>PDF</span>
            </button>
            <button
              onClick={() => {
                const data = filteredClasses.map(cls => ({
                  'Class Name': cls.name,
                  Section: cls.section,
                  'Class Incharge': cls.classInchargeName || 'N/A',
                  Capacity: cls.class_capacity || 0,
                  Enrollment: cls.actualStudentCount || 0,
                  'Fees/Year': cls.feesPerAcademicYear || 0,
                  Terms: cls.numberOfTerms || 0
                }));
                ReportService.generateExcel(data, 'classes_directory.xlsx');
              }}
              className="px-4 py-2.5 text-[#1e293b] hover:bg-white rounded-xl transition-all duration-200 flex items-center gap-2 font-bold text-sm"
            >
              <Download size={16} className="text-emerald-600" />
              <span>Excel</span>
            </button>
          </div>
          <button
            onClick={() => {
              resetForm();
              setIsModalOpen(true);
            }}
            className="primary-gradient-bg text-white px-8 py-3.5 rounded-2xl hover:opacity-90 transition-all duration-300 flex items-center gap-3 font-bold shadow-lg shadow-blue-200/50 hover:scale-[1.02] active:scale-[0.98]"
          >
            <Plus size={24} />
            <span>Add Class</span>
          </button>
        </div>
      </div>

        {/* Filters */}
        <div className="glass-card rounded-[2rem] p-8 premium-shadow border-none animate-slideInUp">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex-1 max-w-md relative group">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Search Classes</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name or section..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none transition-all duration-300 font-semibold text-slate-700"
                />
              </div>
            </div>
            <div className="px-6 py-3 bg-orange-50 rounded-2xl border border-orange-100/50">
              <span className="text-sm font-bold text-orange-600">
                Showing <span className="text-slate-800">{startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredClasses.length)}</span> <span className="text-slate-400 font-medium mx-1">of</span> <span className="text-slate-800">{filteredClasses.length}</span> <span className="text-slate-400 font-medium">classes</span>
              </span>
            </div>
          </div>
        </div>

          <Table
            data={paginatedClasses}
            columns={columns}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            searchable={false}
          />

        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            resetForm();
          }}
          title="Add New Class"
          size="xl"
          footer={
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  resetForm();
                }}
                className="px-4 py-2 text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 active:scale-95"
              >
                Add Class
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Class Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value.toUpperCase()})}
                  placeholder="e.g. GRADE 10"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm"
                  required
                />
              </div>
              <div>
                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Number of Sections *</label>
                <div className="relative w-40" ref={sectionDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsSectionDropdownOpen(!isSectionDropdownOpen)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl flex items-center justify-between bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 group shadow-sm"
                  >
                    <span className="font-bold text-slate-700">
                      {formData.numberOfSections === 0 ? 'No' : formData.numberOfSections}
                    </span>
                    <ChevronDown 
                      size={18} 
                      className={`text-slate-400 transition-transform duration-300 ${isSectionDropdownOpen ? 'rotate-180 text-blue-500' : ''}`}
                    />
                  </button>

                  {isSectionDropdownOpen && (
                    <div className="absolute z-[100] w-full mt-2 bg-white border border-slate-100 rounded-2xl shadow-2xl shadow-blue-900/10 overflow-hidden animate-fadeIn backdrop-blur-xl">
                      <div className="max-h-[240px] overflow-y-auto custom-scrollbar p-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({...formData, numberOfSections: 0});
                            setIsSectionDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm font-bold rounded-xl transition-all duration-200 mb-0.5 ${formData.numberOfSections === 0 ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                        >
                          No
                        </button>
                        {[...Array(30)].map((_, i) => (
                          <button
                            key={i + 1}
                            type="button"
                            onClick={() => {
                              setFormData({...formData, numberOfSections: i + 1});
                              setIsSectionDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 text-sm font-bold rounded-xl transition-all duration-200 mb-0.5 ${formData.numberOfSections === i + 1 ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                          >
                            {i + 1}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">Auto-generates sections A, B, C...</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Class Capacity *</label>
                <input
                  type="number"
                  value={formData.class_capacity}
                  onChange={(e) => setFormData({...formData, class_capacity: e.target.value})}
                  placeholder="Enter capacity"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm"
                  min="1"
                  required
                />
              </div>
                <div>
                  <CustomDatePicker
                    label="Academic Year From"
                    name="academicYearFrom"
                    value={formData.academicYearFrom}
                    onChange={(e) => handleAcademicYearChange('academicYearFrom', e.target.value)}
                    required={true}
                    title="Select Academic Year Start Date"
                  />
                  <p className="mt-1 text-xs text-gray-500">Select the start date of the academic year</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <CustomDatePicker
                    label="Academic Year To"
                    name="academicYearTo"
                    value={formData.academicYearTo}
                    onChange={(e) => handleAcademicYearChange('academicYearTo', e.target.value)}
                    required={true}
                    ref={academicYearToRef}
                    title="Select Academic Year End Date"
                  />
                  <p className="mt-1 text-xs text-gray-500">Select the end date of the academic year</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Fees per Academic Year (₹) *</label>
                <input
                  type="number"
                  value={formData.feesPerAcademicYear}
                  onChange={(e) => handleTotalFeesChange(e.target.value)}
                  placeholder="Enter total annual fees"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div>
                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">Number of Terms *</label>
                <div className="relative w-40" ref={termsDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsTermsDropdownOpen(!isTermsDropdownOpen)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl flex items-center justify-between bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 group shadow-sm text-sm"
                  >
                    <span className="font-bold text-slate-700">
                      {formData.numberOfTerms || 'Select'}
                    </span>
                    <ChevronDown 
                      size={18} 
                      className={`text-slate-400 transition-transform duration-300 ${isTermsDropdownOpen ? 'rotate-180 text-blue-500' : ''}`}
                    />
                  </button>

                  {isTermsDropdownOpen && (
                    <div className="absolute z-[100] w-full mt-2 bg-white border border-slate-100 rounded-2xl shadow-2xl shadow-blue-900/10 overflow-hidden animate-fadeIn backdrop-blur-xl">
                      <div className="max-h-[240px] overflow-y-auto custom-scrollbar p-1.5">
                        {[...Array(12)].map((_, i) => (
                          <button
                            key={i + 1}
                            type="button"
                            onClick={() => {
                              handleNumberOfTermsChange(i + 1);
                              setIsTermsDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm font-bold rounded-xl transition-all duration-200 mb-0.5 ${formData.numberOfTerms === (i + 1) ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                          >
                            {i + 1} {i + 1 === 1 ? 'Term' : 'Terms'}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Term-wise Fees and Due Dates */}
            {formData.numberOfTerms > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-3">Term-wise Fees and Due Dates</h4>
                <p className="text-xs text-blue-600 mb-3">💡 Term fees are automatically calculated when you change the total annual fees or number of terms. You can manually adjust individual term fees if needed.</p>
                <div className="space-y-4">
                  {formData.termFees.map((termFee, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-1">
                          Term {index + 1} Fee (₹) *
                        </label>
                        <input
                          type="number"
                          value={termFee.amount}
                          onChange={(e) => handleTermFeeChange(index, 'amount', e.target.value)}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <div>
                        <CustomDatePicker
                          label={`Term ${index + 1} Due Date`}
                          name={`term_${index}_dueDate`}
                          value={termFee.dueDate}
                          onChange={(e) => handleTermFeeChange(index, 'dueDate', e.target.value)}
                          required={true}
                        />
                      </div>
                    </div>
                  ))}
                  <div className="border-t border-blue-200 pt-2 mt-2">
                    <div className="flex justify-between items-center font-medium">
                      <span className="text-blue-800">Total Annual Fees:</span>
                      <span className="text-blue-800">
                        ₹{(formData.termFees.reduce((sum, term) => sum + (term.amount ? Number(term.amount) : 0), 0) || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal>

        <ConfirmModal
          isOpen={isConfirmModalOpen}
          onClose={() => {
            setIsConfirmModalOpen(false);
            setClassToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Delete Class"
          message="Are you sure you want to delete this class? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />

        <Loader 
          isLoading={isCreating} 
          message="Creating class..." 
          size="large" 
          overlay={true} 
        />
      </div>
    );
};

export default Classes;